import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBuilderStore } from '../store/builderStore';
import { ComponentRenderer } from '../components/Builder/ComponentRenderer';
import { initAllPixels } from '../utils/pixelTracking';
import { X } from 'lucide-react';

export const Preview: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get('id');
  const { pages, currentPage, setCurrentPage } = useBuilderStore();

  useEffect(() => {
    if (pageId) {
      // If we have a pageId from URL, load that specific page
      const page = pages.find(p => p.id === pageId);
      if (page) {
        setCurrentPage(page);
        
        // Initialize tracking pixels with safe fallback
        if (page.settings?.analytics) {
          const analytics = {
            googleAnalyticsId: page.settings.analytics.googleAnalyticsId,
            facebookPixelIds: page.settings.analytics.facebookPixelIds || [],
            tiktokPixelId: page.settings.analytics.tiktokPixelId,
          };
          initAllPixels(analytics);
        }
      } else {
        navigate('/dashboard');
      }
    } else if (currentPage) {
      // If no pageId but we have currentPage from store, use it
      if (currentPage.settings?.analytics) {
        const analytics = {
          googleAnalyticsId: currentPage.settings.analytics.googleAnalyticsId,
          facebookPixelIds: currentPage.settings.analytics.facebookPixelIds || [],
          tiktokPixelId: currentPage.settings.analytics.tiktokPixelId,
        };
        initAllPixels(analytics);
      }
    } else {
      // No page data at all, redirect to dashboard
      navigate('/dashboard');
    }
  }, [pageId, pages, currentPage, setCurrentPage, navigate]);

  if (!currentPage) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  const layoutWidth = currentPage.settings.layoutWidth ?? 'mobile';
  const maxPageWidth = currentPage.settings.maxPageWidth ?? 480;
  const outerBg = currentPage.settings.outerBackgroundColor || '#0b0f1a';
  const isMobileLayout = layoutWidth === 'mobile';

  return (
    <div
      className="theme-light-only min-h-screen relative"
      style={{
        background: isMobileLayout
          ? outerBg
          : currentPage.settings.backgroundColor || '#ffffff',
      }}
    >
      {/* Close Preview Button */}
      <button
        onClick={() => navigate('/dashboard')}
        className="fixed top-4 right-4 z-50 p-3 bg-gray-900 hover:bg-gray-800 text-white rounded-full shadow-lg transition-colors"
        title="Close Preview"
      >
        <X size={24} />
      </button>

      {/* Page Title Bar */}
      <div className="fixed top-4 left-4 z-50 bg-gray-900/90 backdrop-blur px-6 py-3 rounded-lg shadow-lg">
        <h1 className="text-white font-semibold">{currentPage.name}</h1>
        <p className="text-gray-400 text-sm">Preview Mode</p>
      </div>

      {/* Page canvas — matches the live published page width */}
      <main
        className="pt-24 pb-12 mx-auto relative min-h-screen"
        style={{
          maxWidth: isMobileLayout ? `${maxPageWidth}px` : undefined,
          background: currentPage.settings.backgroundColor || '#ffffff',
          ...(currentPage.settings.backgroundImage
            ? {
                backgroundImage: `url(${currentPage.settings.backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : {}),
          ...(isMobileLayout
            ? { boxShadow: '0 0 60px rgba(0,0,0,0.35)' }
            : {}),
        }}
      >
        {currentPage.components.length === 0 ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center">
              <p className="text-gray-500 text-lg">No components added yet</p>
              <button
                onClick={() => navigate('/builder')}
                className="mt-4 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Add Components
              </button>
            </div>
          </div>
        ) : (
          currentPage.components.map((component) => (
            <div key={component.id} className="w-full">
              <ComponentRenderer component={component} />
            </div>
          ))
        )}
      </main>
    </div>
  );
};

